import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const ThemeContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Load saved preferences from LocalStorage initially (for immediate render)
    const [primaryColor, setPrimaryColor] = useState(() => localStorage.getItem('primaryColor') || 'orange');
    const [appName, setAppName] = useState(() => localStorage.getItem('appName') || 'Katedral');
    const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('logoUrl') || 'https://i.ibb.co/60m6XbM/LOGO-KATEDRAL-WEB.png');
    const [logoSize, setLogoSize] = useState(() => localStorage.getItem('logoSize') || '40');

    const [homeLogoUrl, setHomeLogoUrl] = useState(() => localStorage.getItem('homeLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png');
    const [homeLogoSize, setHomeLogoSize] = useState(() => localStorage.getItem('homeLogoSize') || '120');

    const [modalLogoUrl, setModalLogoUrl] = useState(() => localStorage.getItem('modalLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png');

    const [modalLogoSize, setModalLogoSize] = useState(() => localStorage.getItem('modalLogoSize') || '80');

    const [checkinLogoUrl, setCheckinLogoUrl] = useState(() => localStorage.getItem('checkinLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png');
    const [checkinLogoSize, setCheckinLogoSize] = useState(() => localStorage.getItem('checkinLogoSize') || '120'); // Legacy/Fallback
    const [checkinLoginLogoSize, setCheckinLoginLogoSize] = useState(() => localStorage.getItem('checkinLoginLogoSize') || '120');
    const [checkinMenuLogoSize, setCheckinMenuLogoSize] = useState(() => localStorage.getItem('checkinMenuLogoSize') || '60');

    // Logo Margins
    const [logoMarginTop, setLogoMarginTop] = useState(() => localStorage.getItem('logoMarginTop') || '0');
    const [logoMarginBottom, setLogoMarginBottom] = useState(() => localStorage.getItem('logoMarginBottom') || '0');

    const [homeLogoMarginTop, setHomeLogoMarginTop] = useState(() => localStorage.getItem('homeLogoMarginTop') || '0');
    const [homeLogoMarginBottom, setHomeLogoMarginBottom] = useState(() => localStorage.getItem('homeLogoMarginBottom') || '0');

    const [modalLogoMarginTop, setModalLogoMarginTop] = useState(() => localStorage.getItem('modalLogoMarginTop') || '0');
    const [modalLogoMarginBottom, setModalLogoMarginBottom] = useState(() => localStorage.getItem('modalLogoMarginBottom') || '0');

    const [checkinLogoMarginTop, setCheckinLogoMarginTop] = useState(() => localStorage.getItem('checkinLogoMarginTop') || '0');
    const [checkinLogoMarginBottom, setCheckinLogoMarginBottom] = useState(() => localStorage.getItem('checkinLogoMarginBottom') || '0');

    const [checkinMenuLogoMarginTop, setCheckinMenuLogoMarginTop] = useState(() => localStorage.getItem('checkinMenuLogoMarginTop') || '0');
    const [checkinMenuLogoMarginBottom, setCheckinMenuLogoMarginBottom] = useState(() => localStorage.getItem('checkinMenuLogoMarginBottom') || '0');

    // SEO Settings
    const [seoTitle, setSeoTitle] = useState(() => localStorage.getItem('seoTitle') || 'Bar Katedral | Reservas');
    const [seoDescription, setSeoDescription] = useState(() => localStorage.getItem('seoDescription') || 'Bar Katedral - Reservas de Aniversário e Eventos');
    const [seoKeywords, setSeoKeywords] = useState(() => localStorage.getItem('seoKeywords') || 'bar, katedral, reservas, aniversário, eventos, balada');
    const [seoImage, setSeoImage] = useState(() => localStorage.getItem('seoImage') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png');

    // Icons
    const [appIconUrl, setAppIconUrl] = useState(() => localStorage.getItem('appIconUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png');
    const [faviconUrl, setFaviconUrl] = useState(() => localStorage.getItem('faviconUrl') || 'favicon.ico');

    // General Settings
    const [address, setAddress] = useState(() => localStorage.getItem('address') || 'Rua Gonçalves Dias, 123 - Centro');
    const [whatsappNumber, setWhatsappNumber] = useState(() => localStorage.getItem('whatsappNumber') || '5511999999999');

    const [loading, setLoading] = useState(false);  // Start false - use localStorage values immediately, API updates in background

    // Define color palettes
    const colors = {
        orange: {
            primary: '249 115 22', // orange-500
            primaryForeground: '255 255 255',
            ring: '249 115 22',
        },
        blue: {
            primary: '59 130 246', // blue-500
            primaryForeground: '255 255 255',
            ring: '59 130 246',
        },
        purple: {
            primary: '168 85 247', // purple-500
            primaryForeground: '255 255 255',
            ring: '168 85 247',
        },
        green: {
            primary: '34 197 94', // green-500
            primaryForeground: '255 255 255',
            ring: '34 197 94',
        },
        red: {
            primary: '239 68 68', // red-500
            primaryForeground: '255 255 255',
            ring: '239 68 68',
        },
        pink: {
            primary: '236 72 153', // pink-500
            primaryForeground: '255 255 255',
            ring: '236 72 153',
        },
    };

    const migrationAttempted = React.useRef(false);

    // Update DOM for Icons
    useEffect(() => {
        const getFullUrl = (url) => {
            if (!url) return '';
            if (url.startsWith('http') || url.startsWith('data:')) return url;

            let baseUrl = BACKEND_URL;

            // Smart Local Dev: If accessing via local network IP, use that IP for backend too
            // This fixes mobile access when BACKEND_URL is localhost or a tunnel
            const hostname = window.location.hostname;
            if (hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
                baseUrl = `http://${hostname}:8001`;
            }

            // Remove trailing slash from base
            baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            // Ensure path starts with slash
            const path = url.startsWith('/') ? url : `/${url}`;

            return `${baseUrl}${path}`;
        };

        // Update Favicon
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = getFullUrl(faviconUrl);

        // Update App Icon (Apple Touch Icon)
        let appleLink = document.querySelector("link[rel='apple-touch-icon']");
        if (!appleLink) {
            appleLink = document.createElement('link');
            appleLink.rel = 'apple-touch-icon';
            document.getElementsByTagName('head')[0].appendChild(appleLink);
        }
        appleLink.href = getFullUrl(appIconUrl);

    }, [faviconUrl, appIconUrl]);

    // Fetch settings from API on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API}/public/appearance`);
                const settings = response.data;

                if (settings) {
                    setAppName(settings.app_name || 'Katedral');
                    setPrimaryColor(settings.primary_color);
                    setLogoUrl(settings.logo_url);
                    setLogoSize(settings.logo_size);
                    setHomeLogoUrl(settings.home_logo_url);
                    setHomeLogoSize(settings.home_logo_size);
                    setModalLogoUrl(settings.modal_logo_url);
                    setModalLogoSize(settings.modal_logo_size);
                    setCheckinLogoUrl(settings.checkin_logo_url || settings.logo_url);
                    setCheckinLogoSize(settings.checkin_logo_size || '120');
                    setCheckinLoginLogoSize(settings.checkin_login_logo_size || '120');
                    setCheckinMenuLogoSize(settings.checkin_menu_logo_size || '60');

                    // Margins
                    setLogoMarginTop(settings.logo_margin_top || '0');
                    setLogoMarginBottom(settings.logo_margin_bottom || '0');
                    setHomeLogoMarginTop(settings.home_logo_margin_top || '0');
                    setHomeLogoMarginBottom(settings.home_logo_margin_bottom || '0');
                    setModalLogoMarginTop(settings.modal_logo_margin_top || '0');
                    setModalLogoMarginBottom(settings.modal_logo_margin_bottom || '0');
                    setCheckinLogoMarginTop(settings.checkin_logo_margin_top || '0');
                    setCheckinLogoMarginBottom(settings.checkin_logo_margin_bottom || '0');
                    setCheckinMenuLogoMarginTop(settings.checkin_menu_logo_margin_top || '0');
                    setCheckinMenuLogoMarginBottom(settings.checkin_menu_logo_margin_bottom || '0');

                    // SEO
                    setSeoTitle(settings.seo_title || 'Bar Katedral | Reservas');
                    setSeoDescription(settings.seo_description || 'Bar Katedral - Reservas de Aniversário e Eventos');
                    setSeoKeywords(settings.seo_keywords || 'bar, katedral, reservas, aniversário, eventos, balada');
                    setSeoImage(settings.seo_image_url || settings.logo_url);

                    // Icons
                    setAppIconUrl(settings.app_icon_url || settings.logo_url);
                    setFaviconUrl(settings.favicon_url || 'favicon.ico');

                    // General
                    setAddress(settings.address || 'Rua Gonçalves Dias, 123 - Centro');
                    setWhatsappNumber(settings.whatsapp_number || '5511999999999');
                }
            } catch (error) {
                console.error('Error fetching appearance settings:', error);

                // Auto-migration: If settings not found (404) and user is admin, save current local settings to DB
                if (error.response && error.response.status === 404) {
                    // Prevent double execution (React Strict Mode)
                    if (migrationAttempted.current) return;
                    migrationAttempted.current = true;

                    const token = localStorage.getItem('admin_token');
                    if (token) {
                        console.log('Migrating local settings to database...');
                        const currentSettings = {
                            app_name: localStorage.getItem('appName') || 'Katedral',
                            primary_color: localStorage.getItem('primaryColor') || 'orange',
                            logo_url: localStorage.getItem('logoUrl') || 'https://i.ibb.co/60m6XbM/LOGO-KATEDRAL-WEB.png',
                            logo_size: localStorage.getItem('logoSize') || '40',
                            home_logo_url: localStorage.getItem('homeLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png',
                            home_logo_size: localStorage.getItem('homeLogoSize') || '120',
                            modal_logo_url: localStorage.getItem('modalLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png',
                            modal_logo_size: localStorage.getItem('modalLogoSize') || '80',
                            checkin_logo_url: localStorage.getItem('checkinLogoUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png',
                            checkin_logo_size: localStorage.getItem('checkinLogoSize') || '120',
                            checkin_login_logo_size: localStorage.getItem('checkinLoginLogoSize') || '120',
                            checkin_menu_logo_size: localStorage.getItem('checkinMenuLogoSize') || '60',

                            // Margins
                            logo_margin_top: localStorage.getItem('logoMarginTop') || '0',
                            logo_margin_bottom: localStorage.getItem('logoMarginBottom') || '0',
                            home_logo_margin_top: localStorage.getItem('homeLogoMarginTop') || '0',
                            home_logo_margin_bottom: localStorage.getItem('homeLogoMarginBottom') || '0',
                            modal_logo_margin_top: localStorage.getItem('modalLogoMarginTop') || '0',
                            modal_logo_margin_bottom: localStorage.getItem('modalLogoMarginBottom') || '0',
                            checkin_logo_margin_top: localStorage.getItem('checkinLogoMarginTop') || '0',
                            checkin_logo_margin_bottom: localStorage.getItem('checkinLogoMarginBottom') || '0',
                            checkin_menu_logo_margin_top: localStorage.getItem('checkinMenuLogoMarginTop') || '0',
                            checkin_menu_logo_margin_bottom: localStorage.getItem('checkinMenuLogoMarginBottom') || '0',

                            // SEO
                            seo_title: localStorage.getItem('seoTitle') || 'Bar Katedral | Reservas',
                            seo_description: localStorage.getItem('seoDescription') || 'Bar Katedral - Reservas de Aniversário e Eventos',
                            seo_keywords: localStorage.getItem('seoKeywords') || 'bar, katedral, reservas, aniversário, eventos, balada',
                            seo_image_url: localStorage.getItem('seoImage') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png',

                            // Icons
                            app_icon_url: localStorage.getItem('appIconUrl') || 'https://i.ibb.co/RTxg3Sjg/LOGO-KATEDRAL-WEB.png',
                            favicon_url: localStorage.getItem('faviconUrl') || 'favicon.ico',

                            // General
                            address: localStorage.getItem('address') || 'Rua Gonçalves Dias, 123 - Centro',
                            whatsapp_number: localStorage.getItem('whatsappNumber') || '5511999999999',
                        };

                        try {
                            await axios.post(`${API}/admin/appearance`, currentSettings, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            // toast.success('Configurações migradas para o banco de dados!', { id: 'migration-success' });
                        } catch (saveError) {
                            console.error('Error migrating settings:', saveError);
                            migrationAttempted.current = false; // Reset on failure to allow retry
                        }
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    // Apply CSS variables
    useEffect(() => {
        const root = window.document.documentElement;
        const colorPalette = colors[primaryColor];

        if (colorPalette) {
            root.style.setProperty('--primary', colorPalette.primary);
            root.style.setProperty('--primary-foreground', colorPalette.primaryForeground);
            root.style.setProperty('--ring', colorPalette.ring);
        }
    }, [primaryColor]);

    // Save settings to API
    const saveSettings = async (newSettings) => {
        try {
            const token = localStorage.getItem('admin_token');
            if (!token) return; // Only admins can save

            await axios.post(`${API}/admin/appearance`, newSettings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Aparência atualizada!');
        } catch (error) {
            console.error('Error saving appearance settings:', error);
            toast.error('Erro ao salvar aparência');
        }
    };

    // Update LocalStorage and API
    const updateState = (key, value, setter) => {
        setter(value);
        localStorage.setItem(key, value);

        // Prepare full settings object for API save
        // We use the current state for other values, but override the one being changed
        // Note: State updates are async, so we can't rely on 'primaryColor' variable immediately if we just called setPrimaryColor
        // But for this simple implementation, we'll construct the object with the new value

        const currentSettings = {
            app_name: key === 'appName' ? value : appName,
            primary_color: key === 'primaryColor' ? value : primaryColor,
            logo_url: key === 'logoUrl' ? value : logoUrl,
            logo_size: key === 'logoSize' ? value : logoSize,
            home_logo_url: key === 'homeLogoUrl' ? value : homeLogoUrl,
            home_logo_size: key === 'homeLogoSize' ? value : homeLogoSize,
            modal_logo_url: key === 'modalLogoUrl' ? value : modalLogoUrl,
            modal_logo_size: key === 'modalLogoSize' ? value : modalLogoSize,
            checkin_logo_url: key === 'checkinLogoUrl' ? value : checkinLogoUrl,
            checkin_logo_size: key === 'checkinLogoSize' ? value : checkinLogoSize,
            checkin_login_logo_size: key === 'checkinLoginLogoSize' ? value : checkinLoginLogoSize,
            checkin_menu_logo_size: key === 'checkinMenuLogoSize' ? value : checkinMenuLogoSize,

            // Margins
            logo_margin_top: key === 'logoMarginTop' ? value : logoMarginTop,
            logo_margin_bottom: key === 'logoMarginBottom' ? value : logoMarginBottom,
            home_logo_margin_top: key === 'homeLogoMarginTop' ? value : homeLogoMarginTop,
            home_logo_margin_bottom: key === 'homeLogoMarginBottom' ? value : homeLogoMarginBottom,
            modal_logo_margin_top: key === 'modalLogoMarginTop' ? value : modalLogoMarginTop,
            modal_logo_margin_bottom: key === 'modalLogoMarginBottom' ? value : modalLogoMarginBottom,
            checkin_logo_margin_top: key === 'checkinLogoMarginTop' ? value : checkinLogoMarginTop,
            checkin_logo_margin_bottom: key === 'checkinLogoMarginBottom' ? value : checkinLogoMarginBottom,
            checkin_menu_logo_margin_top: key === 'checkinMenuLogoMarginTop' ? value : checkinMenuLogoMarginTop,
            checkin_menu_logo_margin_bottom: key === 'checkinMenuLogoMarginBottom' ? value : checkinMenuLogoMarginBottom,

            // SEO
            seo_title: key === 'seoTitle' ? value : seoTitle,
            seo_description: key === 'seoDescription' ? value : seoDescription,
            seo_keywords: key === 'seoKeywords' ? value : seoKeywords,
            seo_image_url: key === 'seoImage' ? value : seoImage,

            // Icons
            app_icon_url: key === 'appIconUrl' ? value : appIconUrl,
            favicon_url: key === 'faviconUrl' ? value : faviconUrl,

            // General
            address: key === 'address' ? value : address,
            whatsapp_number: key === 'whatsappNumber' ? value : whatsappNumber,
        };

        // Debounce save to API to avoid too many requests
        clearTimeout(window.saveTimeout);
        window.saveTimeout = setTimeout(() => {
            saveSettings(currentSettings);
        }, 500);
    };

    // Unified update function to avoid race conditions
    const updateAppearanceSettings = (updates) => {
        // updates example: { logoUrl: '...', logoSize: '...' }

        const newSettings = {
            app_name: updates.appName !== undefined ? updates.appName : appName,
            primary_color: updates.primaryColor !== undefined ? updates.primaryColor : primaryColor,
            logo_url: updates.logoUrl !== undefined ? updates.logoUrl : logoUrl,
            logo_size: updates.logoSize !== undefined ? updates.logoSize : logoSize,
            home_logo_url: updates.homeLogoUrl !== undefined ? updates.homeLogoUrl : homeLogoUrl,
            home_logo_size: updates.homeLogoSize !== undefined ? updates.homeLogoSize : homeLogoSize,
            modal_logo_url: updates.modalLogoUrl !== undefined ? updates.modalLogoUrl : modalLogoUrl,
            modal_logo_size: updates.modalLogoSize !== undefined ? updates.modalLogoSize : modalLogoSize,
            checkin_logo_url: updates.checkinLogoUrl !== undefined ? updates.checkinLogoUrl : checkinLogoUrl,
            checkin_logo_size: updates.checkinLogoSize !== undefined ? updates.checkinLogoSize : checkinLogoSize,
            checkin_login_logo_size: updates.checkinLoginLogoSize !== undefined ? updates.checkinLoginLogoSize : checkinLoginLogoSize,
            checkin_menu_logo_size: updates.checkinMenuLogoSize !== undefined ? updates.checkinMenuLogoSize : checkinMenuLogoSize,

            // Margins
            logo_margin_top: updates.logoMarginTop !== undefined ? updates.logoMarginTop : logoMarginTop,
            logo_margin_bottom: updates.logoMarginBottom !== undefined ? updates.logoMarginBottom : logoMarginBottom,
            home_logo_margin_top: updates.homeLogoMarginTop !== undefined ? updates.homeLogoMarginTop : homeLogoMarginTop,
            home_logo_margin_bottom: updates.homeLogoMarginBottom !== undefined ? updates.homeLogoMarginBottom : homeLogoMarginBottom,
            modal_logo_margin_top: updates.modalLogoMarginTop !== undefined ? updates.modalLogoMarginTop : modalLogoMarginTop,
            modal_logo_margin_bottom: updates.modalLogoMarginBottom !== undefined ? updates.modalLogoMarginBottom : modalLogoMarginBottom,
            checkin_logo_margin_top: updates.checkinLogoMarginTop !== undefined ? updates.checkinLogoMarginTop : checkinLogoMarginTop,
            checkin_logo_margin_bottom: updates.checkinLogoMarginBottom !== undefined ? updates.checkinLogoMarginBottom : checkinLogoMarginBottom,
            checkin_menu_logo_margin_top: updates.checkinMenuLogoMarginTop !== undefined ? updates.checkinMenuLogoMarginTop : checkinMenuLogoMarginTop,
            checkin_menu_logo_margin_bottom: updates.checkinMenuLogoMarginBottom !== undefined ? updates.checkinMenuLogoMarginBottom : checkinMenuLogoMarginBottom,

            // SEO
            seo_title: updates.seoTitle !== undefined ? updates.seoTitle : seoTitle,
            seo_description: updates.seoDescription !== undefined ? updates.seoDescription : seoDescription,
            seo_keywords: updates.seoKeywords !== undefined ? updates.seoKeywords : seoKeywords,
            seo_image_url: updates.seoImage !== undefined ? updates.seoImage : seoImage,

            // Icons
            app_icon_url: updates.appIconUrl !== undefined ? updates.appIconUrl : appIconUrl,
            favicon_url: updates.faviconUrl !== undefined ? updates.faviconUrl : faviconUrl,

            // General
            address: updates.address !== undefined ? updates.address : address,
            whatsapp_number: updates.whatsappNumber !== undefined ? updates.whatsappNumber : whatsappNumber,
        };

        // Update local state and localStorage
        if (updates.appName !== undefined) { setAppName(updates.appName); localStorage.setItem('appName', updates.appName); }
        if (updates.primaryColor !== undefined) { setPrimaryColor(updates.primaryColor); localStorage.setItem('primaryColor', updates.primaryColor); }
        if (updates.logoUrl !== undefined) { setLogoUrl(updates.logoUrl); localStorage.setItem('logoUrl', updates.logoUrl); }
        if (updates.logoSize !== undefined) { setLogoSize(updates.logoSize); localStorage.setItem('logoSize', updates.logoSize); }
        if (updates.homeLogoUrl !== undefined) { setHomeLogoUrl(updates.homeLogoUrl); localStorage.setItem('homeLogoUrl', updates.homeLogoUrl); }
        if (updates.homeLogoSize !== undefined) { setHomeLogoSize(updates.homeLogoSize); localStorage.setItem('homeLogoSize', updates.homeLogoSize); }
        if (updates.modalLogoUrl !== undefined) { setModalLogoUrl(updates.modalLogoUrl); localStorage.setItem('modalLogoUrl', updates.modalLogoUrl); }
        if (updates.modalLogoSize !== undefined) { setModalLogoSize(updates.modalLogoSize); localStorage.setItem('modalLogoSize', updates.modalLogoSize); }
        if (updates.checkinLogoUrl !== undefined) { setCheckinLogoUrl(updates.checkinLogoUrl); localStorage.setItem('checkinLogoUrl', updates.checkinLogoUrl); }
        if (updates.checkinLogoSize !== undefined) { setCheckinLogoSize(updates.checkinLogoSize); localStorage.setItem('checkinLogoSize', updates.checkinLogoSize); }
        if (updates.checkinLoginLogoSize !== undefined) { setCheckinLoginLogoSize(updates.checkinLoginLogoSize); localStorage.setItem('checkinLoginLogoSize', updates.checkinLoginLogoSize); }
        if (updates.checkinMenuLogoSize !== undefined) { setCheckinMenuLogoSize(updates.checkinMenuLogoSize); localStorage.setItem('checkinMenuLogoSize', updates.checkinMenuLogoSize); }

        // Margins
        if (updates.logoMarginTop !== undefined) { setLogoMarginTop(updates.logoMarginTop); localStorage.setItem('logoMarginTop', updates.logoMarginTop); }
        if (updates.logoMarginBottom !== undefined) { setLogoMarginBottom(updates.logoMarginBottom); localStorage.setItem('logoMarginBottom', updates.logoMarginBottom); }
        if (updates.homeLogoMarginTop !== undefined) { setHomeLogoMarginTop(updates.homeLogoMarginTop); localStorage.setItem('homeLogoMarginTop', updates.homeLogoMarginTop); }
        if (updates.homeLogoMarginBottom !== undefined) { setHomeLogoMarginBottom(updates.homeLogoMarginBottom); localStorage.setItem('homeLogoMarginBottom', updates.homeLogoMarginBottom); }
        if (updates.modalLogoMarginTop !== undefined) { setModalLogoMarginTop(updates.modalLogoMarginTop); localStorage.setItem('modalLogoMarginTop', updates.modalLogoMarginTop); }
        if (updates.modalLogoMarginBottom !== undefined) { setModalLogoMarginBottom(updates.modalLogoMarginBottom); localStorage.setItem('modalLogoMarginBottom', updates.modalLogoMarginBottom); }
        if (updates.checkinLogoMarginTop !== undefined) { setCheckinLogoMarginTop(updates.checkinLogoMarginTop); localStorage.setItem('checkinLogoMarginTop', updates.checkinLogoMarginTop); }
        if (updates.checkinLogoMarginBottom !== undefined) { setCheckinLogoMarginBottom(updates.checkinLogoMarginBottom); localStorage.setItem('checkinLogoMarginBottom', updates.checkinLogoMarginBottom); }
        if (updates.checkinMenuLogoMarginTop !== undefined) { setCheckinMenuLogoMarginTop(updates.checkinMenuLogoMarginTop); localStorage.setItem('checkinMenuLogoMarginTop', updates.checkinMenuLogoMarginTop); }
        if (updates.checkinMenuLogoMarginBottom !== undefined) { setCheckinMenuLogoMarginBottom(updates.checkinMenuLogoMarginBottom); localStorage.setItem('checkinMenuLogoMarginBottom', updates.checkinMenuLogoMarginBottom); }

        // SEO
        if (updates.seoTitle !== undefined) { setSeoTitle(updates.seoTitle); localStorage.setItem('seoTitle', updates.seoTitle); }
        if (updates.seoDescription !== undefined) { setSeoDescription(updates.seoDescription); localStorage.setItem('seoDescription', updates.seoDescription); }
        if (updates.seoKeywords !== undefined) { setSeoKeywords(updates.seoKeywords); localStorage.setItem('seoKeywords', updates.seoKeywords); }
        if (updates.seoImage !== undefined) { setSeoImage(updates.seoImage); localStorage.setItem('seoImage', updates.seoImage); }

        // Icons
        if (updates.appIconUrl !== undefined) { setAppIconUrl(updates.appIconUrl); localStorage.setItem('appIconUrl', updates.appIconUrl); }
        if (updates.faviconUrl !== undefined) { setFaviconUrl(updates.faviconUrl); localStorage.setItem('faviconUrl', updates.faviconUrl); }

        // General
        if (updates.address !== undefined) { setAddress(updates.address); localStorage.setItem('address', updates.address); }
        if (updates.whatsappNumber !== undefined) { setWhatsappNumber(updates.whatsappNumber); localStorage.setItem('whatsappNumber', updates.whatsappNumber); }

        // Save to API immediately (no debounce needed for manual save button)
        saveSettings(newSettings);
    };

    const setColor = (color) => {
        if (colors[color]) {
            updateState('primaryColor', color, setPrimaryColor);
        }
    };

    const setLogo = (url) => updateState('logoUrl', url, setLogoUrl);
    const setLogoSizeWrapper = (size) => updateState('logoSize', size, setLogoSize);
    const setLogoMarginTopWrapper = (val) => updateState('logoMarginTop', val, setLogoMarginTop);
    const setLogoMarginBottomWrapper = (val) => updateState('logoMarginBottom', val, setLogoMarginBottom);

    const setHomeLogo = (url) => updateState('homeLogoUrl', url, setHomeLogoUrl);
    const setHomeLogoSizeWrapper = (size) => updateState('homeLogoSize', size, setHomeLogoSize);
    const setHomeLogoMarginTopWrapper = (val) => updateState('homeLogoMarginTop', val, setHomeLogoMarginTop);
    const setHomeLogoMarginBottomWrapper = (val) => updateState('homeLogoMarginBottom', val, setHomeLogoMarginBottom);

    const setModalLogo = (url) => updateState('modalLogoUrl', url, setModalLogoUrl);
    const setModalLogoSizeWrapper = (size) => updateState('modalLogoSize', size, setModalLogoSize);
    const setModalLogoMarginTopWrapper = (val) => updateState('modalLogoMarginTop', val, setModalLogoMarginTop);
    const setModalLogoMarginBottomWrapper = (val) => updateState('modalLogoMarginBottom', val, setModalLogoMarginBottom);

    const setCheckinLogo = (url) => updateState('checkinLogoUrl', url, setCheckinLogoUrl);
    const setCheckinLogoSizeWrapper = (size) => updateState('checkinLogoSize', size, setCheckinLogoSize);
    const setCheckinLoginLogoSizeWrapper = (size) => updateState('checkinLoginLogoSize', size, setCheckinLoginLogoSize);
    const setCheckinMenuLogoSizeWrapper = (size) => updateState('checkinMenuLogoSize', size, setCheckinMenuLogoSize);

    const setCheckinLogoMarginTopWrapper = (val) => updateState('checkinLogoMarginTop', val, setCheckinLogoMarginTop);
    const setCheckinLogoMarginBottomWrapper = (val) => updateState('checkinLogoMarginBottom', val, setCheckinLogoMarginBottom);
    const setCheckinMenuLogoMarginTopWrapper = (val) => updateState('checkinMenuLogoMarginTop', val, setCheckinMenuLogoMarginTop);
    const setCheckinMenuLogoMarginBottomWrapper = (val) => updateState('checkinMenuLogoMarginBottom', val, setCheckinMenuLogoMarginBottom);

    const setAppIcon = (url) => updateState('appIconUrl', url, setAppIconUrl);
    const setFavicon = (url) => updateState('faviconUrl', url, setFaviconUrl);

    return (
        <ThemeContext.Provider value={{
            primaryColor, setColor, colors,
            appName, setAppName,
            logoUrl, setLogo, logoSize, setLogoSize: setLogoSizeWrapper,
            logoMarginTop, setLogoMarginTop: setLogoMarginTopWrapper,
            logoMarginBottom, setLogoMarginBottom: setLogoMarginBottomWrapper,

            homeLogoUrl, setHomeLogo, homeLogoSize, setHomeLogoSize: setHomeLogoSizeWrapper,
            homeLogoMarginTop, setHomeLogoMarginTop: setHomeLogoMarginTopWrapper,
            homeLogoMarginBottom, setHomeLogoMarginBottom: setHomeLogoMarginBottomWrapper,

            modalLogoUrl, setModalLogo, modalLogoSize, setModalLogoSize: setModalLogoSizeWrapper,
            modalLogoMarginTop, setModalLogoMarginTop: setModalLogoMarginTopWrapper,
            modalLogoMarginBottom, setModalLogoMarginBottom: setModalLogoMarginBottomWrapper,

            checkinLogoUrl, setCheckinLogo, checkinLogoSize, setCheckinLogoSize: setCheckinLogoSizeWrapper,
            checkinLoginLogoSize, setCheckinLoginLogoSize: setCheckinLoginLogoSizeWrapper,
            checkinMenuLogoSize, setCheckinMenuLogoSize: setCheckinMenuLogoSizeWrapper,

            checkinLogoMarginTop, setCheckinLogoMarginTop: setCheckinLogoMarginTopWrapper,
            checkinLogoMarginBottom, setCheckinLogoMarginBottom: setCheckinLogoMarginBottomWrapper,
            checkinMenuLogoMarginTop, setCheckinMenuLogoMarginTop: setCheckinMenuLogoMarginTopWrapper,
            checkinMenuLogoMarginBottom, setCheckinMenuLogoMarginBottom: setCheckinMenuLogoMarginBottomWrapper,

            seoTitle, setSeoTitle,
            seoDescription, setSeoDescription,
            seoKeywords, setSeoKeywords,
            seoImage, setSeoImage,

            appIconUrl, setAppIcon,
            faviconUrl, setFavicon,

            address, setAddress,
            whatsappNumber, setWhatsappNumber,
            updateAppearanceSettings, // Expose new function
            loading
        }}>
            {children}
        </ThemeContext.Provider>
    );
};
